"use client";

import { FC, useCallback, useState, useEffect } from "react";
import Image from "next/image";
import useEmblaCarousel from "embla-carousel-react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

interface SliderItem {
  image: string;
  alt?: string;
}

interface SliderProps {
  items: SliderItem[];
}

const Slider: FC<SliderProps> = ({ items }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
  });
  const [activeIndex, setActiveIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <section
      className="relative w-full bg-white py-8 sm:py-12"
      role="region"
      aria-labelledby="slider-heading"
    >
      <div className="relative container mx-auto px-4">
        {/* Prev Button */}
        <button
          onClick={scrollPrev}
          className="hidden sm:flex absolute left-2 sm:left-0 top-1/2 z-10 p-2 transform -translate-y-1/2 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full shadow text-white hover:bg-gradient-to-r hover:from-[#ECD05B] hover:to-[#AE7D2F] transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          aria-label="Previous slide"
          aria-controls="embla-viewport"
        >
          <FaChevronLeft size={20} aria-hidden="true" />
        </button>

        {/* Embla Viewport */}
        <div
          className="overflow-hidden"
          ref={emblaRef}
          role="region"
          aria-roledescription="carousel"
          aria-label="Image carousel"
          id="embla-viewport"
        >
          <div className="flex">
            {items.map((item, idx) => (
              <div
                key={idx}
                className="flex-none w-1/2 min-w-[100%] sm:min-w-[50%] px-2 block_container cursor-pointer mx-auto sm:mx-0"
                onClick={() => scrollTo(idx)}
                role="group"
                aria-roledescription="slide"
                aria-label={`Slide ${idx + 1} of ${items.length}`}
                id={`slide-${idx}`}
              >
                <div className="relative aspect-video overflow-hidden rounded-lg">
                  <Image
                    src={item.image}
                    alt={item.alt ?? `Slide ${idx + 1}`}
                    fill
                    className="object-cover"
                    sizes="(max-width: 480px) 100vw, (max-width: 768px) 90vw, 50vw"
                    quality={75} // Optimized quality
                    loading={idx < 2 ? "eager" : "lazy"}
                    placeholder="blur"
                    blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkbHB0eH/xAAVAQEBAAAAAAAAAAAAAAAAAAAAAf/EABRAQEAAAAAAAAAAAAAAAAAAAAD/2gAMAwEAAhEDEQA/AKrSdTtHQi4Gf5PX/9k="
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Next Button */}
        <button
          onClick={scrollNext}
          className="hidden sm:flex absolute right-2 sm:right-0 top-1/2 z-10 p-2 transform -translate-y-1/2 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full shadow text-white hover:bg-gradient-to-r hover:from-[#ECD05B] hover:to-[#AE7D2F] transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          aria-label="Next slide"
          aria-controls="embla-viewport"
        >
          <FaChevronRight size={20} aria-hidden="true" />
        </button>

        {/* Dots */}
        <div
          className="flex justify-center gap-3 mt-4"
          role="tablist"
          aria-label="Carousel pagination"
        >
          {items.map((_, idx) => (
            <button
              key={idx}
              onClick={() => scrollTo(idx)}
              className={`
                w-4 h-4 rounded-full cursor-pointer transition-all duration-300 min-h-[44px] min-w-[44px] flex items-center justify-center
                ${
                  activeIndex === idx
                    ? "bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B]"
                    : "bg-gray-400 hover:bg-gray-500"
                }
              `}
              role="tab"
              aria-selected={activeIndex === idx}
              aria-controls={`slide-${idx}`}
              aria-label={`Go to slide ${idx + 1} of ${items.length}`}
            >
              <span className="sr-only">Slide {idx + 1}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Slider;